import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar,
} from "recharts";
import toast, { Toaster } from "react-hot-toast";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function formatValue(val) {
  return val ?? "-";
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

// ─── Color Palette ────────────────────────────────────────────────────────────
const CHART_COLORS = {
  blue: "#3b82f6",
  green: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  purple: "#8b5cf6",
  sky: "#0ea5e9",
  slate: "#64748b",
  teal: "#14b8a6",
  indigo: "#6366f1",
  pink: "#ec4899",
  orange: "#f97316",
  cyan: "#06b6d4",
};

const PIE_COLORS = [CHART_COLORS.blue, CHART_COLORS.slate];
const INCIDENT_SEVERITY_COLORS = {
  high: CHART_COLORS.red,
  medium: CHART_COLORS.amber,
  low: CHART_COLORS.green,
  critical: "#dc2626",
};

const PIE_MULTI_COLORS = [
  CHART_COLORS.blue,
  CHART_COLORS.green,
  CHART_COLORS.amber,
  CHART_COLORS.purple,
  CHART_COLORS.sky,
  CHART_COLORS.teal,
  CHART_COLORS.red,
  CHART_COLORS.slate,
  CHART_COLORS.indigo,
  CHART_COLORS.pink,
];

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl px-4 py-3 shadow-lg text-[13px]"
      style={{ fontFamily: FONT_UI }}
    >
      {label && <p className="text-[var(--text-muted)] mb-1 text-[11px]">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} className="font-extrabold" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
}

// ─── Chart Card Wrapper ───────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] ${className}`}
    >
      <div className="mb-4">
        <h4
          className="m-0 text-[17px] font-extrabold text-[var(--text-primary)]"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h4>
        {subtitle && (
          <p className="mt-0.5 text-[11px] text-[var(--text-muted)]" style={{ fontFamily: FONT_UI }}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

export default function Summaries() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [scanDetails, setScanDetails] = useState([]);
  const [incidentDetails, setIncidentDetails] = useState([]);
  const [emergencyDetails, setEmergencyDetails] = useState([]);
  const [guardReports, setGuardReports] = useState([]);
  const [routeReports, setRouteReports] = useState([]);

  const [filters, setFilters] = useState({
    date_range: "this_week",
    start_date: "",
    end_date: "",
  });

  const [dateError, setDateError] = useState("");
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchData(token);
  }, []);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const validateDates = () => {
    if (filters.date_range !== "custom") return true;
    
    if (!filters.start_date || !filters.end_date) {
      setDateError("Please select both start and end dates.");
      return false;
    }

    const start = new Date(filters.start_date);
    const end = new Date(filters.end_date);

    if (start > end) {
      setDateError("Start date cannot be after end date.");
      return false;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 365) {
      setDateError("Date range cannot exceed 365 days.");
      return false;
    }

    setDateError("");
    return true;
  };

  const fetchData = async (token) => {
    try {
      setLoading(true);
      const token = getToken();
      
      let url = `${API_BASE_URL}/reports/summary?date_range=${filters.date_range}`;
      if (filters.date_range === "custom" && filters.start_date && filters.end_date) {
        url += `&start_date=${filters.start_date}&end_date=${filters.end_date}`;
      }

      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to fetch data");
      }

      const data = await res.json();

      setStats(data.stats || {});
      setScanDetails(data.scanDetails || []);
      setIncidentDetails(data.incidentDetails || []);
      setEmergencyDetails(data.emergencyDetails || []);
      setGuardReports(data.guardReports || []);
      setRouteReports(data.routeReports || []);
      
      toast.success("Data loaded successfully!");
    } catch (err) {
      console.error("Summary fetch error:", err);
      toast.error(err.message || "Failed to load summary data.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    if (name === "date_range" && value !== "custom") {
      setDateError("");
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    
    if (!validateDates()) {
      toast.error(dateError);
      return;
    }
    
    fetchData();
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  // ── Derived chart data ──────────────────────────────────────────────────────

  // 1. Guard performance bar chart
  const guardChartData = guardReports.map((r) => ({
    name: r.guard_name || "Unknown",
    Scans: Number(r.total_scans) || 0,
    Checkpoints: Number(r.checkpoints_covered) || 0,
    Routes: Number(r.routes_covered) || 0,
  }));

  // 2. Route coverage bar chart
  const routeChartData = routeReports.map((r) => ({
    name: r.route_name || "Unknown",
    Scans: Number(r.total_scans) || 0,
    Guards: Number(r.guards_assigned) || 0,
    Checkpoints: Number(r.checkpoints_covered) || 0,
  }));

  // 3. Scan activity over time (group by date)
  const scanByDate = scanDetails.reduce((acc, row) => {
    const date = row.scan_date ? String(row.scan_date).slice(0, 10) : "Unknown";
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});
  const scanTimelineData = Object.entries(scanByDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, Scans: count }));

  // 4. Coverage pie: checkpoints covered vs not
  const coveredCheckpoints = Number(stats.covered_checkpoints) || 0;
  const totalCheckpoints = Number(stats.total_checkpoints) || 0;
  const checkpointPieData = [
    { name: "Covered", value: coveredCheckpoints },
    { name: "Remaining", value: Math.max(0, totalCheckpoints - coveredCheckpoints) },
  ];

  // 5. Coverage pie: routes covered vs not
  const coveredRoutes = Number(stats.covered_routes) || 0;
  const totalRoutes = Number(stats.total_routes) || 0;
  const routePieData = [
    { name: "Covered", value: coveredRoutes },
    { name: "Remaining", value: Math.max(0, totalRoutes - coveredRoutes) },
  ];

  // 6. Incident severity breakdown
  const severityCount = incidentDetails.reduce((acc, row) => {
    const sev = (row.severity || "unknown").toLowerCase();
    acc[sev] = (acc[sev] || 0) + 1;
    return acc;
  }, {});
  const incidentSeverityData = Object.entries(severityCount).map(([sev, count]) => ({
    name: sev.charAt(0).toUpperCase() + sev.slice(1),
    value: count,
    fill: INCIDENT_SEVERITY_COLORS[sev] || CHART_COLORS.slate,
  }));

  // 7. Scans per guard (for scan breakdown pie)
  const scansByGuard = scanDetails.reduce((acc, row) => {
    const g = row.guard_name || "Unknown";
    acc[g] = (acc[g] || 0) + 1;
    return acc;
  }, {});
  const scansByGuardData = Object.entries(scansByGuard)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading summaries...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="summaries"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Summaries Module
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          View reports, statistics, and patrol summaries
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Report Filters
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Filter reports by date range
              </p>
            </div>
          </div>

          <form onSubmit={applyFilters} className="mt-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Date Range
              </label>
              <select
                name="date_range"
                value={filters.date_range}
                onChange={handleChange}
                className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] min-w-[150px]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="custom">Custom</option>
                <option value="all_time">All Time</option>
              </select>
            </div>

            {filters.date_range === "custom" && (
              <>
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleChange}
                    className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                    style={{ fontFamily: FONT_UI }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleChange}
                    className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                    style={{ fontFamily: FONT_UI }}
                  />
                </div>
              </>
            )}

            <div>
              <button
                type="submit"
                className="border-0 rounded-xl px-6 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Apply Filters
              </button>
            </div>
          </form>

          {dateError && (
            <div className="mt-3 text-[13px] text-[var(--red)] font-bold">
              ⚠️ {dateError}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Summary Statistics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Total Guards", value: stats.total_guards, color: "blue" },
              { label: "Active Guards", value: stats.active_guards, color: "green" },
              { label: "Total Routes", value: stats.total_routes, color: "sky" },
              { label: "Covered Routes", value: stats.covered_routes, color: "teal" },
              { label: "Checkpoints", value: stats.total_checkpoints, color: "purple" },
              { label: "Covered CPs", value: stats.covered_checkpoints, color: "green" },
              { label: "Total Scans", value: stats.scanned_qr_codes, color: "blue" },
              { label: "Incidents", value: stats.incidents, color: "amber" },
              { label: "Emergencies", value: stats.emergencies, color: "red" },
              {
                label: "Completion",
                value: `${stats.patrol_completion_rate || 0}%`,
                color: "slate",
              },
            ].map(({ label, value, color }) => (
              <StatCard key={label} label={label} value={value} color={color} />
            ))}
          </div>
        </div>

        {/* ── CHARTS ROW 1: Scan Timeline + Coverage Pies ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          {/* Scan Activity Over Time */}
          <ChartCard
            title="Scan Activity Over Time"
            subtitle="Daily scan count across the selected period"
            className="xl:col-span-2"
          >
            {scanTimelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={scanTimelineData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.blue} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={CHART_COLORS.blue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="Scans"
                    stroke={CHART_COLORS.blue}
                    strokeWidth={2.5}
                    fill="url(#scanGrad)"
                    dot={{ r: 3, fill: CHART_COLORS.blue, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>

          {/* Coverage Pies */}
          <div className="flex flex-col gap-4">
            <ChartCard title="Checkpoint Coverage" subtitle={`${coveredCheckpoints} / ${totalCheckpoints}`}>
              {totalCheckpoints > 0 ? (
                <ResponsiveContainer width="100%" height={110}>
                  <PieChart>
                    <Pie
                      data={checkpointPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={48}
                      paddingAngle={3}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {checkpointPieData.map((entry, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart small />
              )}
              <DonutLegend items={[
                { label: "Covered", color: PIE_COLORS[0], value: coveredCheckpoints },
                { label: "Remaining", color: PIE_COLORS[1], value: Math.max(0, totalCheckpoints - coveredCheckpoints) },
              ]} />
            </ChartCard>

            <ChartCard title="Route Coverage" subtitle={`${coveredRoutes} / ${totalRoutes}`}>
              {totalRoutes > 0 ? (
                <ResponsiveContainer width="100%" height={110}>
                  <PieChart>
                    <Pie
                      data={routePieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={48}
                      paddingAngle={3}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {routePieData.map((entry, i) => (
                        <Cell key={i} fill={[CHART_COLORS.green, CHART_COLORS.slate][i]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart small />
              )}
              <DonutLegend items={[
                { label: "Covered", color: CHART_COLORS.green, value: coveredRoutes },
                { label: "Remaining", color: CHART_COLORS.slate, value: Math.max(0, totalRoutes - coveredRoutes) },
              ]} />
            </ChartCard>
          </div>
        </div>

        {/* ── CHARTS ROW 2: Guard Performance + Scans per Guard ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          <ChartCard
            title="Guard Performance"
            subtitle="Scans, checkpoints & routes covered per guard"
            className="xl:col-span-2"
          >
            {guardChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={guardChartData} margin={{ top: 5, right: 10, left: -20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontFamily: FONT_UI, fontSize: 12, paddingTop: 12 }}
                  />
                  <Bar dataKey="Scans" fill={CHART_COLORS.blue} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Checkpoints" fill={CHART_COLORS.green} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Routes" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>

          {/* Scans by Guard Pie */}
          <ChartCard title="Scans by Guard" subtitle="Share of total scans per guard">
            {scansByGuardData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={scansByGuardData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {scansByGuardData.map((_, i) => (
                        <Cell key={i} fill={PIE_MULTI_COLORS[i % PIE_MULTI_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5 max-h-[120px] overflow-y-auto">
                  {scansByGuardData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px]" style={{ fontFamily: FONT_UI }}>
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: PIE_MULTI_COLORS[i % PIE_MULTI_COLORS.length] }}
                      />
                      <span className="text-[var(--text-muted)] truncate flex-1">{d.name}</span>
                      <span className="font-extrabold text-[var(--text-primary)]">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </div>

        {/* ── CHARTS ROW 3: Route Analysis + Incident Severity ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          <ChartCard
            title="Route-wise Analysis"
            subtitle="Scans, assigned guards & checkpoints per route"
            className="xl:col-span-2"
          >
            {routeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={routeChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fontFamily: FONT_UI, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    width={75}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontFamily: FONT_UI, fontSize: 12, paddingTop: 12 }}
                  />
                  <Bar dataKey="Scans" fill={CHART_COLORS.sky} radius={[0, 4, 4, 0]} maxBarSize={20} />
                  <Bar dataKey="Guards" fill={CHART_COLORS.teal} radius={[0, 4, 4, 0]} maxBarSize={20} />
                  <Bar dataKey="Checkpoints" fill={CHART_COLORS.blue} radius={[0, 4, 4, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>

          {/* Incident Severity */}
          <ChartCard title="Incident Severity" subtitle="Breakdown by severity level">
            {incidentSeverityData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={90}
                    barSize={14}
                    data={incidentSeverityData}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <RadialBar
                      dataKey="value"
                      cornerRadius={6}
                      label={false}
                    >
                      {incidentSeverityData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </RadialBar>
                    <Tooltip content={<CustomTooltip />} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="mt-2 space-y-2">
                  {incidentSeverityData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2" style={{ fontFamily: FONT_UI }}>
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: d.fill }}
                      />
                      <span className="text-[13px] text-[var(--text-muted)] flex-1">{d.name}</span>
                      <span className="text-[13px] font-extrabold text-[var(--text-primary)]">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </div>

        {/* ── DATA TABLES ── */}
        <div className="space-y-4">
          <TableSection title="Scanning Details">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Guard</Th>
                  <Th>Checkpoint</Th>
                  <Th>Route</Th>
                </tr>
              </thead>
              <tbody>
                {scanDetails.length ? (
                  scanDetails.slice(0, 20).map((row, i) => (
                    <tr key={i} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <Td>{formatValue(row.scan_date)}</Td>
                      <Td>{formatValue(row.guard_name)}</Td>
                      <Td>{formatValue(row.checkpoint_name)}</Td>
                      <Td>{formatValue(row.route_name)}</Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={4} />
                )}
              </tbody>
            </table>
          </TableSection>

          <TableSection title="Guard Wise Patrol Report">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Guard</Th>
                  <Th>Total Scans</Th>
                  <Th>Checkpoints Covered</Th>
                  <Th>Routes Covered</Th>
                </tr>
              </thead>
              <tbody>
                {guardReports.length ? (
                  guardReports.map((row, i) => (
                    <tr key={i} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <Td>{formatValue(row.guard_name)}</Td>
                      <Td>{formatValue(row.total_scans)}</Td>
                      <Td>{formatValue(row.checkpoints_covered)}</Td>
                      <Td>{formatValue(row.routes_covered)}</Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={4} />
                )}
              </tbody>
            </table>
          </TableSection>

          <TableSection title="Route Wise Patrol Report">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Route</Th>
                  <Th>Total Scans</Th>
                  <Th>Guards Assigned</Th>
                  <Th>Checkpoints Covered</Th>
                </tr>
              </thead>
              <tbody>
                {routeReports.length ? (
                  routeReports.map((row, i) => (
                    <tr key={i} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <Td>{formatValue(row.route_name)}</Td>
                      <Td>{formatValue(row.total_scans)}</Td>
                      <Td>{formatValue(row.guards_assigned)}</Td>
                      <Td>{formatValue(row.checkpoints_covered)}</Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={4} />
                )}
              </tbody>
            </table>
          </TableSection>

          <TableSection title="Incident Reports">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Guard</Th>
                  <Th>Type</Th>
                  <Th>Description</Th>
                  <Th>Severity</Th>
                </tr>
              </thead>
              <tbody>
                {incidentDetails.length ? (
                  incidentDetails.slice(0, 20).map((row, i) => (
                    <tr key={i} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <Td>{formatValue(row.incident_date)}</Td>
                      <Td>{formatValue(row.guard_name)}</Td>
                      <Td>{formatValue(row.incident_type)}</Td>
                      <Td className="max-w-[200px] truncate">{formatValue(row.description)}</Td>
                      <Td>
                        <SeverityBadge value={row.severity} />
                      </Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={5} />
                )}
              </tbody>
            </table>
          </TableSection>

          <TableSection title="Emergency Alerts">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Guard</Th>
                  <Th>Type</Th>
                  <Th>Description</Th>
                  <Th>Priority</Th>
                </tr>
              </thead>
              <tbody>
                {emergencyDetails.length ? (
                  emergencyDetails.slice(0, 20).map((row, i) => (
                    <tr key={i} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <Td>{formatValue(row.emergency_date)}</Td>
                      <Td>{formatValue(row.guard_name)}</Td>
                      <Td>{formatValue(row.emergency_type)}</Td>
                      <Td className="max-w-[200px] truncate">{formatValue(row.description)}</Td>
                      <Td>
                        <SeverityBadge value={row.priority} />
                      </Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={5} />
                )}
              </tbody>
            </table>
          </TableSection>
        </div>
      </div>
    </Layout>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  const colorMap = {
    blue: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6]",
    green: "bg-[rgba(16,185,129,0.12)] text-[#10b981]",
    sky: "bg-[rgba(14,165,233,0.12)] text-[#0ea5e9]",
    teal: "bg-[rgba(20,184,166,0.12)] text-[#14b8a6]",
    purple: "bg-[rgba(139,92,246,0.12)] text-[#8b5cf6]",
    amber: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]",
    red: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]",
    slate: "bg-[rgba(100,116,139,0.12)] text-[#64748b]",
  };
  return (
    <div className={`rounded-xl px-4 py-4 border ${colorMap[color] || colorMap.slate}`}>
      <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-75 m-0" style={{ fontFamily: FONT_UI }}>
        {label}
      </p>
      <p className="text-[26px] font-extrabold m-0 mt-0.5" style={{ fontFamily: FONT_HEADING }}>
        {formatValue(value)}
      </p>
    </div>
  );
}

function DonutLegend({ items }) {
  return (
    <div className="flex gap-4 mt-2 justify-center">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5 text-[12px]" style={{ fontFamily: FONT_UI }}>
          <span className="w-2 h-2 rounded-full" style={{ background: item.color }} />
          <span className="text-[var(--text-muted)]">{item.label}</span>
          <span className="font-extrabold text-[var(--text-primary)]">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function SeverityBadge({ value }) {
  if (!value) return <span className="text-[var(--text-muted)] text-[13px]">-</span>;
  const v = String(value).toLowerCase();
  const styles = {
    critical: "bg-[rgba(220,38,38,0.12)] border border-[rgba(220,38,38,0.28)] text-[#dc2626]",
    high: "bg-[rgba(239,68,68,0.12)] border border-[rgba(239,68,68,0.28)] text-[#ef4444]",
    medium: "bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.28)] text-[#f59e0b]",
    low: "bg-[rgba(16,185,129,0.12)] border border-[rgba(16,185,129,0.28)] text-[#10b981]",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold ${styles[v] || "bg-[var(--surface)] text-[var(--text-muted)]"}`}
      style={{ fontFamily: FONT_UI }}
    >
      {value}
    </span>
  );
}

function EmptyChart({ small = false }) {
  return (
    <div
      className={`flex items-center justify-center text-[var(--text-muted)] text-[13px] rounded-xl bg-[var(--surface)] ${
        small ? "h-[110px]" : "h-[200px]"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      No data available
    </div>
  );
}

function TableSection({ title, children }) {
  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-x-auto">
      <h3
        className="m-0 mb-4 text-[18px] font-extrabold text-[var(--text-primary)]"
        style={{ fontFamily: FONT_HEADING }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] bg-[var(--surface)] first:rounded-tl-lg last:rounded-tr-lg"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-4 py-3 text-[13px] text-[var(--text-primary)] border-b border-[var(--divider)]"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function EmptyRow({ col }) {
  return (
    <tr>
      <td
        colSpan={col}
        className="text-center py-8 text-[var(--text-muted)] text-[13px]"
        style={{ fontFamily: FONT_UI }}
      >
        No data available
      </td>
    </tr>
  );
}